'use client'

import { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect, useRef } from 'react'

// Cookie consent types following Italian GDPR guidelines
type CookieConsent = {
  // Technical cookies - always allowed, no consent needed
  technical: boolean // Always true (session, authentication, security)
  // Analytics cookies - requires consent (PostHog, Google Analytics)
  analytics: boolean
  // Profiling cookies - requires consent (marketing, advertising)
  profiling: boolean
  // Timestamp of consent
  timestamp: string | null
  // Consent given status
  hasConsented: boolean
}

const defaultConsent: CookieConsent = {
  technical: true,
  analytics: false,
  profiling: false,
  timestamp: null,
  hasConsented: false,
}

const CONSENT_STORAGE_KEY = 'kennydb_cookie_consent'

type CookieConsentContextType = {
  consent: CookieConsent
  acceptAll: () => void
  rejectAll: () => void
  acceptTechnicalOnly: () => void
  updateConsent: (analytics: boolean, profiling: boolean) => void
  showBanner: boolean
  setShowBanner: (show: boolean) => void
  openSettings: () => void
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined)

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  // Use refs to track initialization state without triggering re-renders in effects
  const consentRef = useRef<CookieConsent>(defaultConsent)
  const showBannerRef = useRef(true)

  const [consent, setConsentState] = useState<CookieConsent>(defaultConsent)
  const [showBanner, setShowBannerState] = useState(true)
  const [mounted, setMounted] = useState(false)

  // Effect for mounting - only sets mounted flag, no cascading state
  useEffect(() => {
    setMounted(true)
  }, [])

  // Effect for loading stored consent - updates refs first, then state in callback
  useEffect(() => {
    if (!mounted) return

    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as CookieConsent
        consentRef.current = parsed
        showBannerRef.current = false
        // Single batched state update after ref update
        setConsentState(parsed)
        setShowBannerState(false)
      }
    } catch {
      // Invalid stored consent, keep defaults
    }
  }, [mounted])

  const saveConsent = useCallback((newConsent: CookieConsent) => {
    const consentWithTimestamp = {
      ...newConsent,
      timestamp: new Date().toISOString(),
      hasConsented: true,
    }
    consentRef.current = consentWithTimestamp
    setConsentState(consentWithTimestamp)
    setShowBannerState(false)
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentWithTimestamp))
  }, [])

  const acceptAll = useCallback(() => {
    saveConsent({
      technical: true,
      analytics: true,
      profiling: true,
      timestamp: null,
      hasConsented: false,
    })
  }, [saveConsent])

  const rejectAll = useCallback(() => {
    saveConsent({
      technical: true,
      analytics: false,
      profiling: false,
      timestamp: null,
      hasConsented: false,
    })
  }, [saveConsent])

  const acceptTechnicalOnly = useCallback(() => {
    rejectAll()
  }, [rejectAll])

  const updateConsent = useCallback((analytics: boolean, profiling: boolean) => {
    saveConsent({
      technical: true,
      analytics,
      profiling,
      timestamp: null,
      hasConsented: false,
    })
  }, [saveConsent])

  const openSettings = useCallback(() => {
    setShowBannerState(true)
  }, [])

  const contextValue = useMemo(() => ({
    consent,
    acceptAll,
    rejectAll,
    acceptTechnicalOnly,
    updateConsent,
    showBanner,
    setShowBanner: setShowBannerState,
    openSettings,
  }), [consent, acceptAll, rejectAll, acceptTechnicalOnly, updateConsent, showBanner])

  // During SSR/initial mount, render children with default values to maintain
  // consistent hook call order in the component tree (avoids "more hooks than
  // previous render" error)
  const value = mounted ? contextValue : {
    consent: defaultConsent,
    acceptAll: () => {},
    rejectAll: () => {},
    acceptTechnicalOnly: () => {},
    updateConsent: () => {},
    showBanner: false,
    setShowBanner: () => {},
    openSettings: () => {},
  }

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext)
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}